import { action, runInAction } from 'mobx';
import { SessionStore } from './session.store';
import { AggregateFunction, AgtAnalyticsStore, SelectColumn } from './agt.analytics.store';

export interface IAgtKvmModel {
	vtnrId: number;
	bm?: number;
	anzahlBm?: number;
	anzahlAnrufeGesamt: number;
	anzahlAnrufeWaehrendServicezeiten: number;
	ziel1Schaden: number;
	ziel2Vertragsanliegen: number;
	ziel3SonstigeAnliegen: number;
	ziel3DannRetoure: number;
	retoure: number;
	keineTaste: number;
	schnellauflege: number;
}

export class AgtKvmModel implements IAgtKvmModel {
	vtnrId: number;
	bm?: number;
	anzahlBm?: number;
	anzahlAnrufeGesamt: number;
	anzahlAnrufeWaehrendServicezeiten: number;
	ziel1Schaden: number;
	ziel2Vertragsanliegen: number;
	ziel3SonstigeAnliegen: number;
	ziel3DannRetoure: number;
	retoure: number;
	keineTaste: number;
	schnellauflege: number;
	vuInbound?: number;

	// Legacy compatibility properties
	agtId: number;
	kw?: number;

	constructor(data: any) {
		this.vtnrId = data.aid || 0;
		this.bm = data.bm ? parseInt(data.bm) : undefined;
		this.anzahlBm = data.anzahl_bm ? parseInt(data.anzahl_bm) : undefined;
		this.anzahlAnrufeGesamt = data.aag || 0;
		this.anzahlAnrufeWaehrendServicezeiten = data.aasz || 0;
		this.ziel1Schaden = data.z1s || 0;
		this.ziel2Vertragsanliegen = data.z2v || 0;
		this.ziel3SonstigeAnliegen = data.z3sa || 0;
		this.ziel3DannRetoure = data.z3dr || 0;
		this.retoure = data.r || 0;
		this.keineTaste = data.kt || 0;
		this.schnellauflege = data.sa || 0;

		this.vuInbound = this.ziel1Schaden + this.ziel2Vertragsanliegen + this.retoure + this.keineTaste;

		// Legacy compatibility - alias properties
		this.agtId = this.vtnrId;
		this.kw = this.bm;
	}
}

export class AgtKvmStore extends AgtAnalyticsStore {
	constructor(session: SessionStore) {
		super(session, {
			tableName: 'agentur_kvm',
			defaultColumns: [
				{ column: 'vtnr_id', aggregateFunction: AggregateFunction.NONE, alias: 'aid' },
				{ column: 'bm', aggregateFunction: AggregateFunction.NONE },
				{ column: 'anzahl_anrufe_gesamt', aggregateFunction: AggregateFunction.NONE, alias: 'aag' },
				{ column: 'anzahl_anrufe_waehrend_servicezeiten', aggregateFunction: AggregateFunction.NONE, alias: 'aasz' },
				{ column: 'ziel1_schaden', aggregateFunction: AggregateFunction.NONE, alias: 'z1s' },
				{ column: 'ziel2_vertragsanliegen', aggregateFunction: AggregateFunction.NONE, alias: 'z2v' },
				{ column: 'ziel3_sonstige_anliegen', aggregateFunction: AggregateFunction.NONE, alias: 'z3sa' },
				{ column: 'ziel3_dann_retoure', aggregateFunction: AggregateFunction.NONE, alias: 'z3dr' },
				{ column: 'retoure', aggregateFunction: AggregateFunction.NONE, alias: 'r' },
				{ column: 'keine_taste', aggregateFunction: AggregateFunction.NONE, alias: 'kt' },
				{ column: 'schnellaufleger', aggregateFunction: AggregateFunction.NONE, alias: 'sa' },
			],
		});
		this.queryName = '?kvm';
	}

	async getDistinctBMs(): Promise<number[]> {
		const selectColumns: SelectColumn[] = [
			{ column: 'bm', aggregateFunction: AggregateFunction.NONE, distinct: true },
		];
		const results = await this.query(selectColumns);
		return results.map(data => parseInt(data.bm)).sort((a, b) => a - b);
	}

	@action
	async loadAgtByBm(agtId: number, bm: number): Promise<AgtKvmModel[]> {
		let whereClause = 'a.vtnr_id = ' + agtId + ' AND bm = ' + bm;
		const results = await this.query(this.config.defaultColumns, whereClause);
		return results.map(data => new AgtKvmModel(data));
	}

	@action
	async loadByBm(bm: number): Promise<AgtKvmModel[]> {
		let whereClause = 'bm = ' + bm;
		const results = await this.query(this.config.defaultColumns, whereClause);
		return results.map(data => new AgtKvmModel(data));
	}

	@action
	async aggregateByAgt(minBm?: number, maxBm?: number): Promise<AgtKvmModel[]> {
		let whereClause = '';
		if (minBm && maxBm) {
			whereClause = `bm BETWEEN ${minBm} AND ${maxBm}`;
		} else if (minBm) {
			whereClause = `bm >= ${minBm}`;
		} else if (maxBm) {
			whereClause = `bm <= ${maxBm}`;
		}

		const selectColumns: SelectColumn[] = [
			{ column: 'vtnr_id', aggregateFunction: AggregateFunction.NONE, groupBy: true, alias: 'aid' },
			{ column: 'bm', aggregateFunction: AggregateFunction.COUNT_DISTINCT, alias: 'anzahl_bm' },
			{ column: 'anzahl_anrufe_gesamt', aggregateFunction: AggregateFunction.SUM, alias: 'aag' },
			{ column: 'anzahl_anrufe_waehrend_servicezeiten', aggregateFunction: AggregateFunction.SUM, alias: 'aasz' },
			{ column: 'ziel1_schaden', aggregateFunction: AggregateFunction.SUM, alias: 'z1s' },
			{ column: 'ziel2_vertragsanliegen', aggregateFunction: AggregateFunction.SUM, alias: 'z2v' },
			{ column: 'ziel3_sonstige_anliegen', aggregateFunction: AggregateFunction.SUM, alias: 'z3sa' },
			{ column: 'ziel3_dann_retoure', aggregateFunction: AggregateFunction.SUM, alias: 'z3dr' },
			{ column: 'retoure', aggregateFunction: AggregateFunction.SUM, alias: 'r' },
			{ column: 'keine_taste', aggregateFunction: AggregateFunction.SUM, alias: 'kt' },
			{ column: 'schnellaufleger', aggregateFunction: AggregateFunction.SUM, alias: 'sa' },
		];

		const results = await this.query(selectColumns, whereClause);
		return results.map(data => new AgtKvmModel(data));
	}

	@action
	async loadMonthlyByBmRange(minBm: number, maxBm: number): Promise<AgtKvmModel[]> {
		const whereClause = `bm BETWEEN ${minBm} AND ${maxBm}`;
		const results = await this.query(this.config.defaultColumns, whereClause);
		return results.map(data => new AgtKvmModel(data));
	}
}
